import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useUpdateCommitMessageMutation, type Commit } from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'
import { extractMutationError, parseCommitMessage } from './commit-dialog-utils'

export interface CommitMessageEditDialogProps {
  commit?: Commit
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Edits commit metadata without changing the immutable hash or graph state. */
export function CommitMessageEditDialog({
  commit,
  open,
  onOpenChange
}: CommitMessageEditDialogProps) {
  const [updateCommitMessage, { isLoading, reset }] = useUpdateCommitMessageMutation()
  const commitHash = commit?.hash
  const commitMessage = commit?.message
  const initial = parseCommitMessage(commitMessage)
  const [title, setTitle] = React.useState(initial.title)
  const [description, setDescription] = React.useState(initial.description)
  const [submitError, setSubmitError] = React.useState('')

  React.useEffect(() => {
    // Keep the submitted values mounted through Radix's close animation.
    // The parent clears `commit` as it closes; treating that as new form data
    // flashes empty fields before the dialog leaves the screen.
    if (!open || !commitHash) return
    const next = parseCommitMessage(commitMessage)
    setTitle(next.title)
    setDescription(next.description)
    setSubmitError('')
  }, [commitHash, commitMessage, open])

  React.useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const message = [title.trim(), description.trim()].filter(Boolean).join('\n\n')
  const initialMessage = [initial.title.trim(), initial.description.trim()]
    .filter(Boolean)
    .join('\n\n')
  const submitting = isLoading
  const canSubmit =
    Boolean(commit) && message.length > 0 && message !== initialMessage && !submitting

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!commit || !canSubmit) return
    setSubmitError('')
    try {
      await updateCommitMessage({ hash: commit.hash, message }).unwrap()
      toast.success(`Updated message for ${commit.hash.slice(0, 8)}`)
      onOpenChange(false)
    } catch (err) {
      setSubmitError(extractMutationError(err, 'Could not update the commit message.'))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit commit message</DialogTitle>
          <DialogDescription>
            Updates the message for {commit?.hash.slice(0, 8)} without changing the commit&apos;s
            contents.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            value={title}
            onChange={(event) => {
              setTitle(event.target.value)
              setSubmitError('')
            }}
            placeholder="Title"
            aria-label="Commit title"
            autoComplete="off"
            autoFocus
            disabled={submitting}
          />
          <Textarea
            value={description}
            onChange={(event) => {
              setDescription(event.target.value)
              setSubmitError('')
            }}
            placeholder="Description (optional)"
            aria-label="Commit description"
            rows={3}
            disabled={submitting}
          />

          {submitError ? (
            <p role="alert" className="text-sm text-st-down">
              {submitError}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
              Save message
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
